import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { jotcoachApi } from '@features/jotcoach/store/api';
import { ComplianceRulePreviewCard } from '@features/rules/components/ComplianceRulePreviewCard';
import {
  rulesApi,
  useDisableRuleMutation,
  useEnableRuleMutation,
  useGetRulesQuery,
  useLazyGetRuleQuery,
} from '@features/rules/store/api';
import type { ComplianceRuleDocument, ProviderRuleResponse } from '@features/rules/types';
import { useUserRoles } from '@features/user';
import { Block as BlockIcon, Cancel, CheckCircle, Refresh, Warning } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Paper, Popover, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';
import { useModal } from '@store/hooks/useModal';
import { hasRole } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

interface RuleComplianceAssessment {
  rule_id: string;
  compliant: 'yes' | 'no' | 'warning';
  reason: string;
  relevant_sections: string[] | null;
}

export interface JotdocComplianceData {
  selectedRuleIds: string[];
  complianceAssessments: RuleComplianceAssessment[];
}

interface RuleComplianceAuditViewProps {
  complianceData: JotdocComplianceData | null;
  jotdocId: string;
  encounterId?: string;
  onScrollToSection?: (sectionId: string) => void;
  onRefetchSession?: () => void;
  onReanalysisStart?: () => void;
  onReanalysisEnd?: () => void;
}

const RuleComplianceAuditViewInner: React.FC<RuleComplianceAuditViewProps> = ({
  complianceData,
  jotdocId,
  encounterId,
  onScrollToSection,
  onRefetchSession,
  onReanalysisStart,
  onReanalysisEnd,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { error: errorTracking } = useEventTracking();
  const dispatch = useAppDispatch();
  const userRoles = useUserRoles();
  const isPracticeAdmin = hasRole(userRoles, 'practice_administrator');
   
  const [disableRule, { isLoading: isDisabling }] = useDisableRuleMutation();
  const [enableRule, { isLoading: isEnabling }] = useEnableRuleMutation();
   
  // TODO: Reanalyze endpoint not yet available in OpenAPI spec
  // const [reanalyzeJotdoc, reanalyzeState] =
  //   notesApi.usePostV2ProviderNoteProcessingWorkflowsReanalyzeJotdocMutation();
  // const isReanalyzing = Boolean(reanalyzeState?.isLoading);
  const isReanalyzing = false;

  const handleReanalyze = useCallback(async () => {
    if (!encounterId) {
      enqueueSnackbar('Encounter ID is required to reanalyze', { variant: 'error' });
      return;
    }

    // Notify parent that reanalysis is starting
    if (onReanalysisStart) {
      onReanalysisStart();
    }

    try {
      // TODO: Implement reanalyze endpoint once available in OpenAPI spec
      // await reanalyzeJotdoc({
      //   reanalyzeJotDocRequest: {
      //     encounter_id: encounterId,
      //     jotdoc_id: jotdocId,
      //   },
      // }).unwrap();

      // Invalidate JotCoach session cache to trigger refetch and polling
      // After reanalyze, backend sets rule_compliance_assessments and attestation_edits to undefined
      // Polling will detect this and continue until they're populated again
      dispatch(
        jotcoachApi.util.invalidateTags([
          { type: 'JotCoachSession', id: `encounter-${encounterId}` },
          { type: 'JotCoachSessionList', id: 'ENCOUNTER_LIST' },
        ]),
      );

      // Immediately trigger a refetch to get the updated session data (with undefined fields)
      // This will trigger the polling logic to start
      if (onRefetchSession) {
        onRefetchSession();
      }

      enqueueSnackbar('Reanalysis functionality coming soon', { variant: 'info' });
    } catch (error: unknown) {
      console.error('Failed to reanalyze jotdoc:', error);
      enqueueSnackbar('Failed to start reanalysis', { variant: 'error' });
      // Notify parent that reanalysis failed
      if (onReanalysisEnd) {
        onReanalysisEnd();
      }
      errorTracking.track(
        error instanceof Error ? error : new Error('Unknown error'),
        'reanalyze_jotdoc',
        {
          jotdoc_id: jotdocId,
          encounter_id: encounterId,
        },
      );
    }
  }, [
    encounterId,
    jotdocId,
    enqueueSnackbar,
    errorTracking,
    dispatch,
    onRefetchSession,
    onReanalysisStart,
    onReanalysisEnd,
  ]);

  // Memoize selected rule IDs to ensure stable reference
  const selectedRuleIds = useMemo(
    () => complianceData?.selectedRuleIds || [],
    [complianceData?.selectedRuleIds],
  );

  // Create a map of rule_id -> assessment for quick lookup
  const assessmentMap = useMemo(() => {
    const assessments = complianceData?.complianceAssessments || [];
    const map = new Map<string, RuleComplianceAssessment>();
    assessments.forEach(assessment => {
      map.set(assessment.rule_id, assessment);
    });
    return map;
  }, [complianceData?.complianceAssessments]);

  // Fetch all compliance rules at once, then filter to match selectedRuleIds
  // This is much more efficient than fetching each rule individually
  // Note: API limits per_page to 100, so we fetch page 1 with max items
  const {
    data: allRulesData,
    isLoading: allRulesLoading,
    error: allRulesError,
  } = useGetRulesQuery(
    {
      rule_type: 'compliance',
      per_page: 100, // API max is 100
      page: 1,
    },
    { skip: selectedRuleIds.length === 0 },
  );

  // Lazy query for fetching individual rules that might be missing from the list
  const [fetchRule] = useLazyGetRuleQuery();

  // Fetch missing rules individually (fallback for rules not in the list)
  const [missingRulesData, setMissingRulesData] = useState<
    Map<string, { rule: ComplianceRuleDocument; isGlobal: boolean; isDisabled: boolean }>
  >(new Map());
  const [missingRulesLoading, setMissingRulesLoading] = useState(false);

  useEffect(() => {
    if (!allRulesData || selectedRuleIds.length === 0) {
      return;
    }

    // Find which rule IDs are missing from the fetched list
    const fetchedRuleIds = new Set(allRulesData.items.map(item => item.rule_id));
    const missingRuleIds = selectedRuleIds.filter(id => !fetchedRuleIds.has(id));

    if (missingRuleIds.length === 0) {
      setMissingRulesLoading(false);
      return;
    }

    // Fetch missing rules individually (these will use RTK Query cache if available)
    let isMounted = true;
    setMissingRulesLoading(true);

    const fetchPromises = missingRuleIds.map(async ruleId => {
      try {
        const result = await fetchRule(ruleId).unwrap();
        if (
          isMounted &&
          result &&
          result.rule_type === 'compliance' &&
          'body_md' in result.rule
        ) {
          return {
            ruleId,
            rule: result.rule as ComplianceRuleDocument,
            isGlobal: result.is_global || false,
            isDisabled: result.is_disabled || false,
          };
        }
        return null;
      } catch (error) {
        console.warn(`Failed to fetch rule ${ruleId}:`, error);
        return null;
      }
    });

    Promise.all(fetchPromises)
      .then(results => {
        if (!isMounted) return;

        const rulesMap = new Map<
          string,
          { rule: ComplianceRuleDocument; isGlobal: boolean; isDisabled: boolean }
        >();
        results.forEach(result => {
          if (result) {
            rulesMap.set(result.ruleId, {
              rule: result.rule,
              isGlobal: result.isGlobal,
              isDisabled: result.isDisabled,
            });
          }
        });

        setMissingRulesData(rulesMap);
        setMissingRulesLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setMissingRulesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [allRulesData, selectedRuleIds, fetchRule]);

  // Combine rules from list query and missing rules
  const rulesData = useMemo(() => {
    const rulesMap = new Map<
      string,
      { rule: ComplianceRuleDocument; isGlobal: boolean; isDisabled: boolean }
    >();

    // Add rules from the list query
    if (allRulesData) {
      allRulesData.items.forEach(item => {
        if (
          selectedRuleIds.includes(item.rule_id) &&
          item.rule_type === 'compliance' &&
          'body_md' in item.rule
        ) {
          rulesMap.set(item.rule_id, {
            rule: item.rule as ComplianceRuleDocument,
            isGlobal: item.is_global || false,
            isDisabled: item.is_disabled || false,
          });
        }
      });
    }

    // Add missing rules
    missingRulesData.forEach((value, key) => {
      rulesMap.set(key, value);
    });

    return rulesMap;
  }, [allRulesData, missingRulesData, selectedRuleIds]);

  const rulesLoading = allRulesLoading || missingRulesLoading;
  const rulesError = allRulesError;

  // Combine rules with their compliance assessments
  // Filter out disabled rules - they shouldn't appear in the compliance audit view
  const rulesWithCompliance = useMemo(() => {
    if (rulesData.size === 0 || selectedRuleIds.length === 0) {
      return [];
    }

    return selectedRuleIds
      .map(ruleId => {
        const ruleData = rulesData.get(ruleId);
        const assessment = assessmentMap.get(ruleId);

        if (!ruleData) {
          return null;
        }

        // Skip disabled rules
        if (ruleData.isDisabled) {
          return null;
        }

        return {
          rule: ruleData.rule,
          ruleId,
          assessment,
          isGlobal: ruleData.isGlobal,
          isDisabled: ruleData.isDisabled,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [rulesData, selectedRuleIds, assessmentMap]);

  // Group rules by compliance status
  const groupedRules = useMemo(() => {
    const compliant: typeof rulesWithCompliance = [];
    const warning: typeof rulesWithCompliance = [];
    const nonCompliant: typeof rulesWithCompliance = [];

    rulesWithCompliance.forEach(item => {
      if (!item.assessment) {
        // If no assessment, treat as warning
        warning.push(item);
      } else if (item.assessment.compliant === 'yes') {
        compliant.push(item);
      } else if (item.assessment.compliant === 'warning') {
        warning.push(item);
      } else {
        nonCompliant.push(item);
      }
    });

    return { compliant, warning, nonCompliant };
  }, [rulesWithCompliance]);

  if (!complianceData) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '25vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', px: 2 }}>
          Analyzing compliance rules...
        </Typography>
      </Box>
    );
  }

  if (rulesLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '25vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (rulesError) {
    return <Alert severity="error">Failed to load rules. Please try refreshing the page.</Alert>;
  }

  if (rulesWithCompliance.length === 0 && !rulesLoading) {
    return <Alert severity="info">No rules found for this compliance assessment.</Alert>;
  }

  const ComplianceIndicator = ({ status }: { status: 'yes' | 'no' | 'warning' | undefined }) => {
    if (status === 'yes') {
      return <CheckCircle sx={{ color: 'success.main', fontSize: 14 }} />;
    } else if (status === 'warning') {
      return <Warning sx={{ color: 'warning.main', fontSize: 14 }} />;
    } else {
      return <Cancel sx={{ color: 'error.main', fontSize: 14 }} />;
    }
  };

  const getStatusColor = (status: 'yes' | 'no' | 'warning' | undefined) => {
    if (status === 'yes') return 'success.main';
    if (status === 'warning') return 'warning.main';
    return 'error.main';
  };

  const getStatusText = (status: 'yes' | 'no' | 'warning' | undefined) => {
    if (status === 'yes') return 'Compliant';
    if (status === 'warning') return 'Warning';
    return 'Non-Compliant';
  };

  const RuleListItem = ({
    rule,
    ruleId,
    assessment,
    isGlobal,
    isDisabled,
    defaultStatus,
  }: {
    rule: ComplianceRuleDocument;
    ruleId: string;
    assessment?: RuleComplianceAssessment;
    isGlobal: boolean;
    isDisabled: boolean;
    defaultStatus?: 'yes' | 'no' | 'warning';
  }) => {
    const { open: openModal } = useModal();
    
    const handleItemClick = () => {
      if (
        assessment?.relevant_sections &&
        assessment.relevant_sections.length > 0 &&
        onScrollToSection
      ) {
        // Scroll to the first relevant section
        const firstSectionId = assessment.relevant_sections[0];
        onScrollToSection(firstSectionId);
        // Close popover if open
        setAnchorEl(null);
      }
    };
    
    const handleOpenModal = () => {
      const providerRuleResponse: ProviderRuleResponse = {
        rule,
        rule_id: ruleId,
        rule_type: 'compliance',
        is_global: isGlobal,
        is_disabled: isDisabled,
        organization_id: null,
      };

      openModal(MODAL_TYPES.RULE_DETAIL, {
        rule: providerRuleResponse,
        isAdmin: false,
      });
    };
    
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [popoverTimeout, setPopoverTimeout] = useState<NodeJS.Timeout | null>(null);
    const status = assessment?.compliant || defaultStatus;
    const statusColor = getStatusColor(status);
    const open = Boolean(anchorEl);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
      if (popoverTimeout) {
        clearTimeout(popoverTimeout);
        setPopoverTimeout(null);
      }
      setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
      // Add a small delay to allow moving mouse to popover
      const timeout = setTimeout(() => {
        setAnchorEl(null);
      }, 150);
      setPopoverTimeout(timeout);
    };

    const handlePopoverMouseEnter = () => {
      if (popoverTimeout) {
        clearTimeout(popoverTimeout);
        setPopoverTimeout(null);
      }
    };

    const handlePopoverMouseLeave = () => {
      setAnchorEl(null);
    };

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (popoverTimeout) {
          clearTimeout(popoverTimeout);
        }
      };
    }, [popoverTimeout]);

    return (
      <>
        <Box
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          onClick={handleItemClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.75,
            px: 1,
            borderRadius: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            cursor:
              assessment?.relevant_sections && assessment.relevant_sections.length > 0
                ? 'pointer'
                : 'default',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: statusColor,
            },
          }}
        >
          {/* Status Indicator */}
          <Box sx={{ flexShrink: 0 }}>
            <ComplianceIndicator status={status} />
          </Box>

          {/* Rule Title */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              color: 'text.primary',
              lineHeight: 1.3,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {rule.title || rule.selector_text || (rule.body_md ? rule.body_md.slice(0, 50) + (rule.body_md.length > 50 ? '...' : '') : 'Untitled Rule')}
          </Typography>

          {/* Status Badge */}
          <Box
            sx={{
              fontSize: '0.625rem',
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              bgcolor: `${statusColor}20`,
              color: statusColor,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {getStatusText(status)}
          </Box>
        </Box>

        {/* Hover Popover with Full Card */}
        {open && (
          <Popover
            id={`rule-popover-${ruleId}`}
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
            disableRestoreFocus
            disableAutoFocus
            disableEnforceFocus
            slotProps={{
              paper: {
                onMouseEnter: handlePopoverMouseEnter,
                onMouseLeave: handlePopoverMouseLeave,
                sx: {
                  pointerEvents: 'auto',
                  maxWidth: 400,
                  zIndex: 1300,
                  ml: -2,
                },
              },
            }}
            sx={{
              pointerEvents: 'none',
              zIndex: 1300,
            }}
          >
            <Paper
              sx={{ p: 0, maxWidth: 400 }}
              onMouseEnter={handlePopoverMouseEnter}
              onMouseLeave={handlePopoverMouseLeave}
            >
              <ComplianceRulePreviewCard
                rule={rule}
                isGlobal={isGlobal}
                isDisabled={isDisabled}
                onClick={handleOpenModal}
              />
              {assessment?.reason && (
                <Box
                  sx={{
                    p: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor:
                      assessment.compliant === 'yes'
                        ? 'success.light'
                        : assessment.compliant === 'warning'
                          ? 'warning.light'
                          : 'error.light',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: 'text.primary',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    Compliance Status: {getStatusText(status)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                      lineHeight: 1.4,
                      color: 'text.primary',
                    }}
                  >
                    {assessment.reason}
                  </Typography>
                </Box>
              )}
              {isPracticeAdmin && (
                <Box
                  sx={{
                    p: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  {isDisabled ? (
                    <Button
                      variant="outlined"
                      color="success"
                      size="small"
                      startIcon={<CheckCircle />}
                      onClick={async e => {
                        e.stopPropagation();
                        try {
                           
                          await enableRule({
                            ruleId: ruleId,
                            providerRuleDisableRequest: {
                              scope: 'org',
                            },
                          }).unwrap();
                           
                          // Invalidate cache to refresh rule list, rule details, and search results
                          dispatch(
                            rulesApi.util.invalidateTags([
                              { type: 'Rule', id: ruleId },
                              { type: 'RuleList', id: 'LIST' },
                              { type: 'RuleSearch', id: 'LIST' },
                            ]),
                          );
                          enqueueSnackbar('Rule enabled successfully', { variant: 'success' });
                        } catch (error) {
                          console.error('Failed to enable rule:', error);
                          enqueueSnackbar('Failed to enable rule', { variant: 'error' });
                        }
                      }}
                      disabled={isEnabling}
                      fullWidth
                      sx={{
                        fontSize: '0.75rem',
                        textTransform: 'none',
                      }}
                    >
                      {isEnabling ? 'Enabling...' : 'Enable Rule'}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      startIcon={<BlockIcon />}
                      onClick={async e => {
                        e.stopPropagation();
                        try {
                           
                          await disableRule({
                            ruleId: ruleId,
                            providerRuleDisableRequest: {
                              scope: 'org',
                            },
                          }).unwrap();
                           
                          // Invalidate cache to refresh rule list, rule details, and search results
                          dispatch(
                            rulesApi.util.invalidateTags([
                              { type: 'Rule', id: ruleId },
                              { type: 'RuleList', id: 'LIST' },
                              { type: 'RuleSearch', id: 'LIST' },
                            ]),
                          );
                          enqueueSnackbar('Rule disabled successfully', { variant: 'success' });
                        } catch (error) {
                          console.error('Failed to disable rule:', error);
                          enqueueSnackbar('Failed to disable rule', { variant: 'error' });
                        }
                      }}
                      disabled={isDisabling}
                      fullWidth
                      sx={{
                        fontSize: '0.75rem',
                        textTransform: 'none',
                      }}
                    >
                      {isDisabling ? 'Disabling...' : 'Disable Rule'}
                    </Button>
                  )}
                </Box>
              )}
            </Paper>
          </Popover>
        )}
      </>
    );
  };

  const renderRuleGroup = (
    title: string,
    rules: typeof rulesWithCompliance,
    defaultStatus?: 'yes' | 'no' | 'warning',
  ) => {
    if (rules.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant="caption"
          sx={{
            mb: 0.75,
            fontWeight: 600,
            fontSize: '0.6875rem',
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'block',
          }}
        >
          {title} ({rules.length})
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {rules.map(({ rule, ruleId, assessment, isGlobal, isDisabled }) => (
            <RuleListItem
              key={ruleId}
              rule={rule}
              ruleId={ruleId}
              assessment={assessment}
              isGlobal={isGlobal}
              isDisabled={isDisabled}
              defaultStatus={defaultStatus}
            />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Re Analyze Button */}
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={handleReanalyze}
          disabled={isReanalyzing || !encounterId}
          fullWidth
          sx={{
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          {isReanalyzing ? 'Reanalyzing...' : 'Re Analyze'}
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'background.default',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'divider',
            borderRadius: '4px',
            '&:hover': {
              bgcolor: 'action.disabled',
            },
          },
        }}
      >
        {renderRuleGroup('Attention', groupedRules.nonCompliant)}
        {renderRuleGroup('Warning', groupedRules.warning)}
        {renderRuleGroup('Good', groupedRules.compliant)}
      </Box>
    </Box>
  );
};

// Memoize the component to prevent re-renders when compliance data hasn't changed
export const RuleComplianceAuditView = memo(
  RuleComplianceAuditViewInner,
  (prevProps, nextProps) => {
    // Custom comparison - only re-render if compliance data actually changed
    const prevData = prevProps.complianceData;
    const nextData = nextProps.complianceData;

    // If both are null, no change
    if (prevData === null && nextData === null) return true;

    // If one is null and the other isn't, change
    if (prevData === null || nextData === null) return false;

    // Compare rule IDs
    if (prevData.selectedRuleIds.length !== nextData.selectedRuleIds.length) return false;
    if (!prevData.selectedRuleIds.every((id, i) => id === nextData.selectedRuleIds[i]))
      return false;

    // Compare assessments length
    if (prevData.complianceAssessments.length !== nextData.complianceAssessments.length)
      return false;

    // Compare assessments by stringifying (simple deep compare)
    const prevAssessments = JSON.stringify(prevData.complianceAssessments);
    const nextAssessments = JSON.stringify(nextData.complianceAssessments);
    if (prevAssessments !== nextAssessments) return false;

    // Compare other props
    if (prevProps.jotdocId !== nextProps.jotdocId) return false;
    if (prevProps.encounterId !== nextProps.encounterId) return false;
    if (prevProps.onScrollToSection !== nextProps.onScrollToSection) return false;

    return true;
  },
);
